import React, { useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, Checkbox, Typography, Menu, MenuItem, Divider } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus as PlusIcon } from '../../assets/icons/plus';
import { KeyboardArrowDown as ArrowDownIcon } from '@mui/icons-material';
import { useSelection } from '../../hooks/use-selection';
import { styles } from './styles';
import CustomTable from '../../components/common/CustomTable';
import { UsersFilter } from './Users';
import { columns } from './columns';
import PermissionChecker from '../../components/common/PermissionChecker';
import { hasPermission } from '../../utils/permission';
import {
    clearFilters,
    onPageChange,
    onPageSizeChange,
    onSortChange,
    setFilters,
    setInitialFilters,
} from '../../store/slices/tableController';
import { useSnackbar } from 'notistack';
import { useGetUsersMutation, useSearchUsersMutation } from '../../api/users';

const Users = () => {
    const navigate = useNavigate();
    const params = useParams();
    const dispatch = useDispatch();
    const [users, setUsers] = useState([]);
    const [totalUsers, setTotalUsers] = useState(0);
    const controller = useSelector((state) => state.tableController);
    const [enableOldUsers, setEnableOldUsers] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const { enqueueSnackbar } = useSnackbar();

    const [searchUsers, { isLoading: isSearchLoading }] = useSearchUsersMutation();
    const [getUsers, { isLoading: isGetLoading }] = useGetUsersMutation();

    const isLoading = isSearchLoading || isGetLoading;
    const { user } = useSelector((state) => state.session);
    const isBulkUpload = hasPermission(user.authorities || [], ['ROLE_BASE_PERMISSION_BULK_UPLOAD_USERS_SECUSER']);

    useEffect(() => {
        dispatch(
            setInitialFilters([
                {
                    property: {
                        name: 'userName',
                        type: 'string',
                    },
                    operator: {
                        value: '',
                        typesAccepted: [],
                    },
                    value: '',
                },
            ]),
        );
    }, []);

    const fetchData = async (searched, filters, sort, sortBy, page, pageSize) => {
        try {
            if (searched) {
                const query = new URLSearchParams({});
                if (params.companyId) {
                    query.append('company', params.companyId);
                }
                if (sortBy) {
                    query.append('sort', sortBy);
                    query.append('order', sort);
                }
                query.append('offset', page * pageSize);
                query.append('max', pageSize);
                if (filters.length) {
                    filters.forEach((filter) => query.append(filter.property, filter.value));
                }
                if (enableOldUsers) query.append('old_user', 1);
                const response = await searchUsers(query).unwrap();
                if (!response) return;
                if (response?.status === 'ERROR') throw new Error(response?.message);
                setUsers(response.employeeInstanceList);
                setTotalUsers(response.employeeInstanceCount);
            } else {
                const query = new URLSearchParams({});
                if (params.companyId) {
                    query.append('company', params.companyId);
                }
                if (sortBy) {
                    query.append('sort', sortBy);
                    query.append('order', sort);
                }
                query.append('offset', page * pageSize);
                query.append('max', pageSize);
                if (enableOldUsers) query.append('old_user', 1);
                const response = await getUsers(query).unwrap();
                if (!response) return;
                if (response?.status === 'ERROR') throw new Error(response?.message);
                setUsers(response.employeeInstanceList);
                setTotalUsers(response.employeeInstanceCount);
            }
        } catch (error) {
            if (error?.statusCode === 400) enqueueSnackbar(error?.data?.message, { variant: 'error' });
            else enqueueSnackbar(error?.message || 'Something went wrong!', { variant: 'error' });
        }
    };
    const [selectedUsers, handleSelect, handleSelectAll] = useSelection(users);

    useEffect(() => {
        fetchData(
            controller.searched,
            controller.filters,
            controller.sort,
            controller.sortBy,
            controller.page,
            controller.pageSize,
        );
    }, [
        controller.searched,
        controller.filters,
        controller.sort,
        controller.sortBy,
        controller.page,
        controller.pageSize,
        enableOldUsers,
    ]);

    const handleFiltersApply = (newFilters) => {
        const parsedFilters = newFilters.map((filter) => ({
            property: filter.property.name,
            value: filter.value,
            operator: filter.operator.value,
        }));

        dispatch(setFilters(parsedFilters));
    };

    const handleFiltersClear = () => {
        dispatch(clearFilters());
    };

    const handlePageChange = (newPage) => {
        dispatch(onPageChange(newPage - 1));
    };

    const handlePageSizeChange = (newPageSize) => {
        dispatch(onPageSizeChange(newPageSize));
    };

    const handleSortChange = (event, property) => {
        const isAsc = controller.sortBy === property && controller.sort === 'asc';
        dispatch(
            onSortChange({
                sort: isAsc ? 'desc' : 'asc',
                sortBy: property,
            }),
        );
    };

    const onRowClick = (userId) => {
        navigate(params.companyId ? `/company/${params.companyId}/users/${userId}` : `/users/${userId}`);
    };

    const handleDropdownClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleDropdownClose = () => {
        setAnchorEl(null);
    };

    const handleBulkUploadClick = () => {
        navigate('/users/bulk-upload');
        handleDropdownClose();
    };

    return (
        <Box>
            <Box sx={styles.contentHeaderWrapper}>
                <Typography variant="h4">Users</Typography>

                <PermissionChecker
                    requiredPermissions={['ROLE_BASE_PERMISSION_EMPLOYEE_INDEX_CREATE_SECUSER']}
                    noAuthRedirect={false}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Button
                            color="primary"
                            onClick={() =>
                                navigate(params.companyId ? `/company/${params.companyId}/users/add` : '/users/add')
                            }
                            size="large"
                            startIcon={<PlusIcon fontSize="small" />}
                            variant="contained"
                            sx={{
                                borderTopRightRadius: isBulkUpload ? 0 : '6px',
                                borderBottomRightRadius: isBulkUpload ? 0 : '6px',
                                //borderRight: isBulkUpload ? 'none' : '1px solid rgba(255, 255, 255, 0.3)',
                                height: '40px',
                                minHeight: '40px',
                            }}
                        >
                            Add User
                        </Button>
                        {isBulkUpload && (
                            <>
                                <Box
                                    sx={{
                                        width: '1px',
                                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                        mx: 0,
                                    }}
                                />
                                <Button
                                    color="primary"
                                    onClick={handleDropdownClick}
                                    size="large"
                                    variant="contained"
                                    sx={{
                                        borderTopLeftRadius: 0,
                                        borderBottomLeftRadius: 0,
                                        minWidth: '40px',
                                        px: 1,
                                        height: '40px',
                                        minHeight: '40px',
                                    }}
                                >
                                    <ArrowDownIcon fontSize="small" />
                                </Button>
                                <Menu
                                    anchorEl={anchorEl}
                                    open={Boolean(anchorEl)}
                                    onClose={handleDropdownClose}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'right',
                                    }}
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                >
                                    <MenuItem onClick={handleBulkUploadClick}>Bulk Upload Users</MenuItem>
                                </Menu>
                            </>
                        )}
                    </Box>
                </PermissionChecker>
            </Box>
            <Box sx={styles.contentBodyWrapper}>
                <Card>
                    <CardContent>
                        <Box display={'flex'} justifyContent={'space-between'}>
                            <Box display={'flex'} alignItems={'center'}>
                                <Checkbox
                                    color="secondary"
                                    checked={enableOldUsers}
                                    name="enabled"
                                    disabled={false}
                                    onChange={(event) => setEnableOldUsers(event.target.checked)}
                                />
                                <Typography
                                    variant="h6"
                                    color="textPrimary"
                                    sx={styles.typography}
                                    onClick={() => setEnableOldUsers(!enableOldUsers)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    Show All Users
                                </Typography>
                            </Box>
                            <UsersFilter
                                disabled={false}
                                filters={controller.filters}
                                onFiltersApply={handleFiltersApply}
                                onFiltersClear={handleFiltersClear}
                                selectedUsers={selectedUsers}
                                filterProperties={columns}
                                initialFilters={controller.initialFilters}
                            />
                        </Box>
                        <CustomTable
                            onRowClick={onRowClick}
                            isLoading={isLoading}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                            onSelect={handleSelect}
                            onSelectAll={handleSelectAll}
                            onSortChange={handleSortChange}
                            page={controller.page + 1}
                            columns={columns}
                            rows={users}
                            rowsCount={totalUsers}
                            selectedRows={selectedUsers}
                            sort={controller.sort}
                            sortBy={controller.sortBy}
                            showPagignation
                            keyColumn="userID"
                            action={() =>
                                fetchData(
                                    controller.searched,
                                    controller.filters,
                                    controller.sort,
                                    controller.sortBy,
                                    controller.page,
                                    controller.pageSize,
                                )
                            }
                        />
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};

export default Users;
